import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useManagementDialog from "../../hooks/useManagementDialog";
import { isOcrScopeError } from "../../hooks/useOcrBusinessUnitScope";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import { ocrDuplicateText } from "./ocrDuplicateMessages";
import "./OcrDuplicateResolution.css";

const endpoint = "/api/purchasing/delivery-notes-direct/ocr-duplicate";
const choices = [
  ["attach_original", "Attach original only", "Add the image or PDF from the inbox. Quantities, prices and lines stay unchanged."],
  ["complete_receiving", "Complete matching items", "Copy the reviewed quantities, prices and units only to unambiguously matching items. Other lines and receiving links stay unchanged."],
  ["replace_direct", "Replace this draft", "Replace all lines and adjustments in this direct draft with the reviewed import. The same delivery note is kept; no duplicate is created."],
];
const validId = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;

// The immutable payload is the exact review that caused the duplicate. Preview
// does not write, and the server revalidates both this payload and its version.
export default function OcrDuplicateResolution({ request, scopedApi, onCancel, onResolved, onViewDeliveryNote }) {
  const { languageId, themeId } = useReceivingPopupUi();
  const t = (message, values) => ocrDuplicateText(languageId, message, values);
  const notes = (request.notes || []).filter((note) => validId(note.id));
  const [targetId, setTargetId] = useState(notes.length === 1 ? Number(notes[0].id) : null);
  const [attempt, setAttempt] = useState(0);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState("");
  const [attachOriginal, setAttachOriginal] = useState(false);
  const [error, setError] = useState("");
  const [errorDetail, setErrorDetail] = useState("");
  const [uncertain, setUncertain] = useState(false);
  const [review, setReview] = useState(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const inFlight = useRef(false);
  const resolutionSnapshot = useRef(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const close = () => { if (!inFlight.current) onCancel(); };
  const dialogRef = useManagementDialog(close, busy);

  useEffect(() => {
    if (!targetId) return undefined;
    let cancelled = false;
    const current = () => !cancelled && scopedApi.isCurrent();
    setLoading(true); setPreview(null); setAction(""); setAttachOriginal(false);
    setReview(null); setAcknowledged(false); setError(""); setErrorDetail(""); setUncertain(false);
    resolutionSnapshot.current = null;
    scopedApi.post(`${endpoint}/preview`, { ...request.payload, existing_delivery_note_id: targetId })
      .then(({ data }) => {
        if (!current()) return;
        if (Number(data?.existing_delivery_note?.id) !== targetId || !data?.version || !data.actions) throw new Error("Invalid duplicate preview");
        setPreview(data);
      }).catch((failure) => {
        if (cancelled) return;
        setError(isOcrScopeError(failure) || !scopedApi.isCurrent()
          ? "Your session or permissions changed. Close this dialog and reopen the inbox."
          : "Could not check the existing delivery note. Your review is preserved.");
      }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [targetId, attempt, request, scopedApi]);

  const resolve = async (retryPayload = null) => {
    if (inFlight.current || loading) return;
    if (!retryPayload && (!preview || !preview.actions[action] || (review && !acknowledged))) return;
    if (!scopedApi.isCurrent()) { setError("Your session or permissions changed. Close this dialog and reopen the inbox."); return; }
    inFlight.current = true; setBusy(true); setError(""); setErrorDetail("");
    const payload = retryPayload || {
        ...request.payload, existing_delivery_note_id: targetId, action,
        attach_original: action === "attach_original" || (preview.actions.attach_original === true && attachOriginal),
        version: preview.version,
        ...(review && acknowledged ? { quantity_review_token: review.review_token, quantity_review_acknowledged: true } : {}),
    };
    if (!retryPayload) resolutionSnapshot.current = { payload, preview };
    try {
      const { data } = await scopedApi.post(`${endpoint}/resolve`, payload);
      if (!mounted.current || !scopedApi.isCurrent()) return;
      if (data?.success !== true || Number(data.delivery_note?.id) !== targetId) throw new Error("Update unconfirmed");
      // A refresh failure must not offer the already-applied mutation again.
      setPreview(null); setAction(""); setUncertain(false);
      await onResolved(data);
    } catch (failure) {
      if (!mounted.current) return;
      const data = failure.response?.data;
      if (isOcrScopeError(failure) || !scopedApi.isCurrent()) {
        setPreview(null); setUncertain(false);
        setError("Your session or permissions changed. Close this dialog and reopen the inbox.");
      } else if (["QUANTITY_REVIEW_REQUIRED", "PURCHASE_QUANTITY_REVIEW_REQUIRED"].includes(data?.code)
        && data.review_token && Array.isArray(data.warnings) && data.warnings.length) {
        setPreview(resolutionSnapshot.current?.preview || preview); setUncertain(false);
        setReview(data); setAcknowledged(false);
      } else if (data?.code === "OCR_DUPLICATE_STALE") {
        setPreview(null); setReview(null); setAcknowledged(false); setUncertain(false);
        setError("The delivery note has changed. Check it again before applying anything.");
      } else {
        setPreview(null);
        setUncertain(!failure.response || failure.response.status >= 500);
        setError("Could not confirm the update. Check the existing delivery note before trying again.");
        if (failure.response?.status >= 400 && failure.response.status < 500 && typeof data?.error === "string") setErrorDetail(data.error);
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const note = preview?.existing_delivery_note || notes.find((entry) => Number(entry.id) === targetId);
  const matching = preview?.matching;
  const available = choices.filter(([value]) => preview?.actions?.[value] === true);
  const number = (value) => Number(value || 0).toLocaleString(languageId, { maximumFractionDigits: 8 });
  const itemList = (label, items) => items?.length > 0 && <details><summary>{t(label)}</summary>
    <ul>{items.map((item, index) => <li key={`${item.item_id}-${index}`}>{item.description || `#${item.item_id}`}</li>)}</ul>
  </details>;
  return createPortal(<div className="pilcore-purchasing-overlay ocr-duplicate-overlay" data-theme={themeId}
    onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
    <section className="ocr-duplicate-dialog" role="dialog" aria-modal="true" aria-label={t("This delivery note already exists")} tabIndex={-1} ref={dialogRef}>
      <header><h2>{t("This delivery note already exists")}</h2>
        <p className="ocr-duplicate-hint">{t("Review how to use this import without creating a second delivery note.")}</p></header>
      <div className="ocr-duplicate-body" aria-busy={loading || busy}>
        {notes.length > 1 && <div><p>{t("Choose the existing delivery note to review.")}</p>
          <div className="ocr-duplicate-targets">{notes.map((entry) => <button key={entry.id} type="button" disabled={busy || uncertain}
            aria-pressed={targetId === Number(entry.id)} onClick={() => setTargetId(Number(entry.id))}>
            {entry.delivery_note_number} · #{entry.id}</button>)}</div></div>}
        {note && <section className="ocr-duplicate-note">
          <div><strong>{note.delivery_note_number || request.payload.delivery_note_number} · #{note.id}</strong>
            {preview && <div className="ocr-duplicate-meta">
              <span>{t(({ draft: "Draft", verified: "Verified", cancelled: "Cancelled", verified_with_differences: "Verified with differences", received: "Received", closed: "Closed" })[note.status] || note.status)}</span>
              <span>{t(note.from_receiving ? "From a receiving" : "Direct delivery note")}</span>
              <span>{t("{count} lines", { count: note.line_count })}</span>
              <span>{t(note.has_original ? "Original already attached" : "No original attached")}</span>
            </div>}
          </div>
          {onViewDeliveryNote && <button type="button" disabled={busy} onClick={() => onViewDeliveryNote(Number(note.id))}>{t("Open existing delivery note")}</button>}
        </section>}
        {loading && <p role="status">{t("Checking the existing delivery note…")}</p>}
        {error && <p role="alert" className="ocr-duplicate-error">{t(error)}</p>}
        {uncertain && <p>{t("Check the result using the same request. An already-saved update will not be applied twice.")}</p>}
        {errorDetail && <p>{errorDetail}</p>}
        {preview && <>
          {available.length ? <fieldset className="ocr-duplicate-options" disabled={busy}><legend>{t("Choose an action")}</legend>
            {available.map(([value, title, description]) => <label key={value} className={`ocr-duplicate-choice${action === value ? " ocr-duplicate-choice--selected" : ""}`}>
              <input type="radio" name="ocr-duplicate-action" value={value} checked={action === value}
                onChange={() => { setAction(value); setReview(null); setAcknowledged(false); }} />
              <span><strong>{t(title)}</strong><small>{t(description)}</small></span>
            </label>)}
          </fieldset> : <p>{t("No safe update is available. Open the existing delivery note to review it; this import has not been discarded.")}</p>}
          {action === "complete_receiving" && matching && <section className="ocr-duplicate-matches">
            <strong>{t("{count} matching items", { count: matching.matched_count })}</strong>
            {itemList("Matching items to complete", matching.matched_items)}
            <span>{t("{count} inbox lines will not be copied", { count: matching.unmatched_incoming_count })}</span>
            <span>{t("{count} existing lines will stay unchanged", { count: matching.unmatched_existing_count })}</span>
            {matching.ambiguous_count > 0 && <span>{t("{count} ambiguous matches will not be copied", { count: matching.ambiguous_count })}</span>}
            {itemList("Inbox items not matched", matching.unmatched_incoming_items)}
            {itemList("Existing items not matched", matching.unmatched_existing_items)}
          </section>}
          {action && action !== "attach_original" && preview.actions.attach_original && <label className="ocr-duplicate-check">
            <input type="checkbox" checked={attachOriginal} disabled={busy} onChange={(event) => setAttachOriginal(event.target.checked)} />
            <span>{t("Also attach the original from the inbox")}</span>
          </label>}
          {action && action !== "attach_original" && <p className="ocr-duplicate-hint">{t("Estimated prices will remain estimated; missing prices will remain pending.")}</p>}
          {review && <section className="ocr-duplicate-warning" role="alert">
            <strong>{t("Review quantities, prices and units of measure before continuing.")}</strong>
            <ul>{review.warnings.map((warning, index) => <li key={`${warning.item_id}-${index}`}>
              <strong>{warning.item_name || `#${warning.item_id}`}</strong>
              {(warning.quantity_warning ?? !warning.price_warnings?.length) && <small>{t("Current quantity")}: {number(warning.requested_quantity)} · {t("Historical average")}: {number(warning.average_quantity)} {warning.unit_name_translations?.[languageId] || warning.unit_name}</small>}
              {(warning.price_warnings || []).map((price, priceIndex) => <small key={priceIndex}>{t("Current price")}: {number(price.requested_unit_price)} · {t("Reference price")}: {number(price.average_unit_price)} {price.currency} / {warning.unit_name_translations?.[languageId] || warning.unit_name}</small>)}
            </li>)}</ul>
            <label className="ocr-duplicate-check"><input type="checkbox" checked={acknowledged} disabled={busy} onChange={(event) => setAcknowledged(event.target.checked)} />
              <span>{t("I have checked the quantities, prices and units of measure.")}</span></label>
          </section>}
        </>}
      </div>
      <footer><button type="button" disabled={busy} onClick={close}>{t("Back to review")}</button>
        {error && targetId && <button type="button" disabled={busy || loading || !scopedApi.isCurrent()}
          onClick={() => uncertain ? resolve(resolutionSnapshot.current?.payload) : setAttempt((value) => value + 1)}>{t(uncertain ? "Check update result" : "Check again")}</button>}
        {preview && available.length > 0 && <button className="ocr-duplicate-confirm" type="button" disabled={busy || loading || !action || (review && !acknowledged)} onClick={() => resolve()}>{t(busy ? "Updating…" : "Confirm update")}</button>}
      </footer>
    </section>
  </div>, document.body);
}

export function OcrDuplicateSuccess({ note, onClose, onViewDeliveryNote }) {
  const { languageId, themeId } = useReceivingPopupUi();
  const t = (message, values) => ocrDuplicateText(languageId, message, values);
  const dialogRef = useManagementDialog(onClose, false);
  return createPortal(<div className="pilcore-purchasing-overlay ocr-duplicate-overlay" data-theme={themeId}>
    <section className="ocr-duplicate-dialog" role="dialog" aria-modal="true" aria-label={t("Existing delivery note updated")} ref={dialogRef} tabIndex={-1}>
      <header><h2>{t("Existing delivery note updated")}</h2></header>
      <div className="ocr-duplicate-body"><p role="status">{t("The import is linked to delivery note #{id}. No second delivery note was created.", { id: note.id })}</p></div>
      <footer><button type="button" onClick={onClose}>{t("Close")}</button>
        {onViewDeliveryNote && <button type="button" className="ocr-duplicate-confirm" onClick={() => onViewDeliveryNote(Number(note.id))}>{t("Open existing delivery note")}</button>}
      </footer>
    </section>
  </div>, document.body);
}
